/**
 * Measure / ruler overlay — draws the multi-point ruler: the line, the vertices,
 * and a label per leg reading distance and bearing. The running total is shown by
 * the on-screen readout (MeasureReadout), which calls measureTotalNm. Pure
 * rendering from the points MapView passes in.
 */
import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDistanceNm, rhumbBearingDeg, rhumbDestination, rhumbCircle, formatDistance, formatBearing } from '@/nav';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'measure-ruler';

/** Total ruler length in nautical miles. */
export function measureTotalNm(points: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += rhumbDistanceNm(points[i - 1], points[i]);
  return total;
}

/** A true-distance circle of `radiusNm` around `centre`, as a ring of points
 * (the shared rhumb-circle maths), re-exported for the saved-measures layer. */
export function rangeCircle(centre: [number, number], radiusNm: number, steps = 72): [number, number][] {
  return rhumbCircle(centre, radiusNm, steps);
}

function build(points: [number, number][]): FeatureCollection {
  const features: Feature[] = [];
  points.forEach((pt, i) => {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: pt },
      properties: { kind: 'vertex', first: i === 0, index: i },
    });
  });
  if (points.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: points },
      properties: { kind: 'line' },
    });
    // Range circle: a dashed circle on the START point, radius = straight-line
    // distance start -> last point (the measured range), shown with the bearing
    // line so the user reads bearing (line) and range (circle) at once.
    const radius = rhumbDistanceNm(points[0], points[points.length - 1]);
    if (radius > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: rangeCircle(points[0], radius) },
        properties: { kind: 'circle' },
      });
    }
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const d = rhumbDistanceNm(a, b);
      const brg = rhumbBearingDeg(a, b);
      const mid = rhumbDestination(a, brg, d / 2);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: mid },
        properties: { kind: 'leglabel', label: `${formatDistance(d)} · ${formatBearing(brg)}` },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

export function ensureMeasureLayer(map: MaplibreMap, palette: Palette): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build([]) });
  }
  if (!map.getLayer('measure-line')) {
    map.addLayer({
      id: 'measure-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'line'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
  }
  if (!map.getLayer('measure-circle')) {
    map.addLayer({
      id: 'measure-circle',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'circle'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 1.4, 'line-dasharray': [3, 3], 'line-opacity': 0.8 },
    });
  }
  if (!map.getLayer('measure-vertex')) {
    map.addLayer({
      id: 'measure-vertex',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'vertex'],
      paint: {
        'circle-radius': ['case', ['get', 'first'], 5, 4],
        'circle-color': p.UI_BG,
        'circle-stroke-color': p.UI_ACCENT,
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('measure-leglabel')) {
    map.addLayer({
      id: 'measure-leglabel',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'leglabel'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-offset': [0, -0.8],
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.6 },
    });
  }
}

export function updateMeasureLayer(map: MaplibreMap, points: [number, number][]): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as GeoJSONSource).setData(build(points));
}
